import { useState, useEffect, useRef, useContext, forwardRef } from "react";
import {
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Slide,
  Chip,
  OutlinedInput,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { EventContext } from "../../../contexts/EventContext";
import { MessengerContext } from "../../../contexts/MessengerContext";
import TemplateSelect from "../../Messenger/components/TemplateSelect";
import EmailTemplate from "../../Messenger/components/EmailTemplate";
import NewTemplateDialog from "../../Messenger/components/NewTemplateDialog";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Inner component so state is reset cleanly each time the dialog opens
const Inner = ({ leagueEvent }) => {
  const emailTemplateRef = useRef();
  const { userData } = useContext(EventContext);

  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [template, setTemplate] = useState(null);
  const [recipientFilter, setRecipientFilter] = useState("all");
  const [teamId, setTeamId] = useState("");
  const [recipients, setRecipients] = useState([]);  // user IDs for "custom"
  const [canSend, setCanSend] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveAs, setSaveAs] = useState(false);
  const [sendAt, setSendAt] = useState(null);
  const [channel, setChannel] = useState("email");
  const [sending, setSending] = useState(false);

  // For custom recipient picker
  const [allAttendees, setAllAttendees] = useState([]);
  const [loadingAttendees, setLoadingAttendees] = useState(false);

  const teams = leagueEvent?.teams || [];
  const eventId = leagueEvent?.event?._id;

  // Load templates once userData is available
  useEffect(() => {
    if (userData && templates.length === 0) getTemplates();
  }, [userData]);

  // Load selected template body
  useEffect(() => {
    if (templateId && templateId !== "custom") getTemplate();
    else if (!templateId) setTemplate(null);
  }, [templateId]);

  // Load full attendee list when "custom" is first selected
  useEffect(() => {
    if (recipientFilter === "custom" && allAttendees.length === 0 && eventId) {
      loadAttendees();
    }
  }, [recipientFilter]);

  // Update canSend
  useEffect(() => {
    let filterOk = false;
    if (recipientFilter === "all") filterOk = true;
    else if (recipientFilter === "byTeam") filterOk = !!teamId;
    else if (recipientFilter === "custom") filterOk = recipients.length > 0;
    setCanSend(!!templateId && filterOk);
  }, [templateId, recipientFilter, teamId, recipients]);

  const getTemplates = async () => {
    if (!userData) return;
    const result = await request(`templates/?orgId=${userData?.org?._id}`);
    if (result.success) setTemplates(result.templates);
  };

  const getTemplate = async () => {
    const result = await request(`templates/?templateId=${templateId}`);
    if (result.success) setTemplate(result.template);
  };

  const loadAttendees = async () => {
    setLoadingAttendees(true);
    const result = await request(`leagueevent/attendees?eventId=${eventId}&limit=200`);
    if (result?.success) setAllAttendees(result.attendees);
    setLoadingAttendees(false);
  };

  const handleFilterChange = (value) => {
    setRecipientFilter(value);
    setTeamId("");
    setRecipients([]);
  };

  const handleSave = async (data, saveAsNew = false) => {
    try {
      if (saveAsNew) setSaveAs(true);
      if (!data._id && !data.name) {
        setSaveOpen(true);
        return;
      } else if (!data._id || saveAs) {
        if (!data.subject) { toast.error("Please enter a subject"); return; }
        if (data._id) delete data._id;
        const result = await request("templates/create", "post", data);
        if (result.success) {
          getTemplates();
          setTemplateId(result.templateId);
          toast.success("Template created successfully!");
          setSaveOpen(false);
          setSaveAs(false);
        }
      } else {
        const result = await request("templates/edit", "post", {
          templateId: data._id,
          update: { htmlBody: data.htmlBody, jsonBody: data.jsonBody, subject: data.subject },
        });
        if (result.success) toast.success("Template updated successfully!");
      }
    } catch (e) {
      console.error("Error saving template:", e);
    }
  };

  const handleSend = async (data) => {
    setSending(true);
    try {
      data.leagueEventId = leagueEvent._id;
      data.recipientFilter = recipientFilter;
      data.teamId = teamId || undefined;
      data.recipients = recipients;
      data.channel = channel;
      data.sendAt = sendAt;
      data.template = template?.name;
      const result = await request("templates/sendleague", "post", data);
      if (result?.success) {
        toast.success(result.message || "Message sent successfully");
      }
    } catch (e) {
      console.error("Error sending league message:", e);
    }
    setSending(false);
  };

  const saveNewTemplate = (name) => emailTemplateRef.current?.handleSave(name);

  // Helper: attendee display name
  const attendeeName = (a) =>
    [a.profile?.firstName, a.profile?.lastName].filter(Boolean).join(" ") || a.email || a._id;

  return (
    <MessengerContext.Provider
      value={{
        templates, setTemplates,
        templateId, setTemplateId,
        template, setTemplate,
        recipientFilter, setRecipientFilter,
        recipients, setRecipients,
        canSend,
        sendAt, setSendAt,
        channel, setChannel,
        sending,
      }}
    >
      <DialogContent style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <Typography color="text.secondary">{leagueEvent?.event?.name}</Typography>

        {/* ── Recipient filters ── */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Recipients</InputLabel>
            <Select
              value={recipientFilter}
              label="Recipients"
              onChange={(e) => handleFilterChange(e.target.value)}
            >
              <MenuItem value="all">All Attendees</MenuItem>
              <MenuItem value="byTeam">By Team</MenuItem>
              <MenuItem value="custom">Specific People</MenuItem>
            </Select>
          </FormControl>

          {recipientFilter === "byTeam" && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Team</InputLabel>
              <Select
                value={teamId}
                label="Team"
                onChange={(e) => setTeamId(e.target.value)}
              >
                {teams.map((t) => (
                  <MenuItem key={t.team?._id} value={t.team?._id}>
                    {t.team?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {recipientFilter === "custom" && (
            loadingAttendees ? (
              <CircularProgress size={24} sx={{ mt: 1 }} />
            ) : (
              <FormControl size="small" sx={{ minWidth: 300, maxWidth: 480 }}>
                <InputLabel>People</InputLabel>
                <Select
                  multiple
                  value={recipients}
                  onChange={(e) => setRecipients(e.target.value)}
                  input={<OutlinedInput label="People" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((id) => {
                        const a = allAttendees.find((x) => x.userId?.toString() === id || x._id === id);
                        return <Chip key={id} label={a ? attendeeName(a) : id} size="small" />;
                      })}
                    </Box>
                  )}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  {allAttendees.map((a) => {
                    const id = a.userId?.toString() || a._id;
                    return (
                      <MenuItem key={id} value={id}>
                        {attendeeName(a)}
                        {a.email ? ` — ${a.email}` : ""}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )
          )}
        </Box>

        {/* ── Template picker + editor ── */}
        <Grid container spacing={3} justifyContent="center">
          <Grid
            item
            xs={12}
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}
          >
            <TemplateSelect />
          </Grid>
          <Grid item xs={12}>
            <EmailTemplate
              ref={emailTemplateRef}
              template={template}
              update={handleSave}
              send={handleSend}
              leagueEvent={leagueEvent}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <NewTemplateDialog
        open={saveOpen}
        close={() => setSaveOpen(false)}
        save={(name) => saveNewTemplate(name)}
      />
    </MessengerContext.Provider>
  );
};

const LeagueComposeMsgDialog = ({ open, close, leagueEvent }) => (
  <Dialog open={open} onClose={close} fullScreen TransitionComponent={Transition}>
    <AppBar sx={{ position: "relative" }}>
      <Toolbar>
        <IconButton edge="start" color="inherit" onClick={close} aria-label="close">
          <CloseIcon />
        </IconButton>
        <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
          Compose Message
        </Typography>
      </Toolbar>
    </AppBar>
    {open && <Inner leagueEvent={leagueEvent} />}
  </Dialog>
);

export default LeagueComposeMsgDialog;
